import React from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useThemeMode, toggleThemeMode, DARK } from '../utils/themeMode';

/**
 * THE LIGHT/DARK SWITCH — a sun on the dark theme, a moon on the light one.
 *
 * <h2>The icon shows what pressing it GIVES YOU, not what you are in</h2>
 *
 * Both conventions exist and they are opposites, so the choice has to be deliberate: a sun can
 * mean "you are in light mode" or "press for light mode". This one is a DESTINATION, which is the
 * reading a reader can act on — the current mode is already on screen in every pixel around the
 * button, so an icon restating it carries no information.
 *
 * <p>The tooltip says it in words for the same reason, because the glyph alone is exactly the
 * ambiguity above. `aria-label` carries the destination too, so the control is unambiguous to a
 * screen reader that never sees the glyph at all.
 *
 * <h2>In the APP BAR, beside the user menu</h2>
 *
 * Not on the study toolbar. That bar is forty-odd icons that all do something to the CHART, and a
 * control that restyles the whole application is a different kind of thing — it belongs with the
 * account menu, which is the other one. It is also the only bar a customer sees in every profile.
 *
 * <h2>No local state</h2>
 *
 * The mode lives in {@link themeMode}, so this component holds nothing and cannot drift from the
 * theme actually in force. Pressing it writes through the store, which persists and notifies
 * every subscriber in one render pass — the chart, the handle colours and this icon included.
 */
const ThemeModeToggle = () => {
  const mode = useThemeMode();
  const dark = mode === DARK;
  // Where the press takes you, which is what both the glyph and the words describe.
  const label = dark ? 'Switch to the light theme' : 'Switch to the dark theme';

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={toggleThemeMode}
        aria-label={label}
        data-testid="theme-mode-toggle"
        // `inherit`, so it takes the app bar's own contrast text on both themes rather than
        // pinning a colour that is right on one bar and invisible on the other.
        color="inherit"
        size="large"
      >
        {dark ? (
          // A SUN: press for light. Drawn rather than imported so it takes `currentColor` and
          // stays crisp at any DPI — the same reasoning the toolbar's own drawn glyphs give.
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="4.2" fill="currentColor" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <line
                key={deg}
                x1="12" y1="2.6" x2="12" y2="5.2"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                transform={`rotate(${deg} 12 12)`}
              />
            ))}
          </svg>
        ) : (
          // A CRESCENT MOON: press for dark. One filled path with a bite taken out by a second
          // arc, rather than two overlapping shapes — a crescent cut by an opaque circle only
          // looks right over the one background colour that circle is painted in, and this sits
          // on an app bar whose colour changes.
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M20.3 14.9A8.5 8.5 0 0 1 9.1 3.7a8.5 8.5 0 1 0 11.2 11.2z"
            />
          </svg>
        )}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeModeToggle;
